import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import baseQueryWithReauth from './base';
import {
  CreatePrivateEventLoadDTO,
  PrivateEventBalanceByEventDTO,
  PrivateEventBalanceDTO,
  PrivateEventBalanceLoadDTO,
  PrivateEventConfigDTO,
  UpdatePrivateEventConfigDTO,
  PrivateEventLoadCheckoutDTO,
  PublicCreditPriceDTO,
} from '../../models/private-events';

interface OptionalBusinessContext {
  businessId?: string;
}

const getPrivateEventsBasePath = (businessId?: string) => (
  businessId ? `/businesses/${businessId}/private-events` : '/private-events'
);

const getPrivateEventConfigPath = (businessId?: string) => (
  businessId ? `${getPrivateEventsBasePath(businessId)}/config` : '/private-event-config'
);

const privateEventsApi = createApi({
  reducerPath: 'privateEventsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['private-events-balance', 'private-events-loads', 'private-events-config'],
  endpoints: (build) => ({
    getPrivateEventsBalance: build.query<PrivateEventBalanceDTO | null, OptionalBusinessContext>({
      providesTags: ['private-events-balance'],
      query: ({ businessId }) => ({
        url: `${getPrivateEventsBasePath(businessId)}/balance`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PrivateEventBalanceDTO>): PrivateEventBalanceDTO | null => response.data || null,
    }),
    getPrivateEventsBalanceByEvent: build.query<PrivateEventBalanceByEventDTO[] | null, OptionalBusinessContext>({
      providesTags: ['private-events-balance'],
      query: ({ businessId }) => ({
        url: `${getPrivateEventsBasePath(businessId)}/balance/by-event`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PrivateEventBalanceByEventDTO[]>): PrivateEventBalanceByEventDTO[] | null => response.data || null,
    }),
    getPrivateEventsLoads: build.query<PrivateEventBalanceLoadDTO[] | null, OptionalBusinessContext>({
      providesTags: ['private-events-loads'],
      query: ({ businessId }) => ({
        url: `${getPrivateEventsBasePath(businessId)}/balance/loads`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PrivateEventBalanceLoadDTO[]>): PrivateEventBalanceLoadDTO[] | null => response.data || null,
    }),
    createPrivateEventsLoad: build.mutation<PrivateEventLoadCheckoutDTO | null, OptionalBusinessContext & { dto: CreatePrivateEventLoadDTO }>({
      query: ({ businessId, dto }) => ({
        url: `${getPrivateEventsBasePath(businessId)}/balance/loads`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['private-events-loads', 'private-events-balance'],
      transformResponse: (response: Response<PrivateEventLoadCheckoutDTO>): PrivateEventLoadCheckoutDTO | null => response.data || null,
    }),
    deletePrivateEventsLoad: build.mutation<void, OptionalBusinessContext & { loadId: string }>({
      query: ({ businessId, loadId }) => ({
        url: `${getPrivateEventsBasePath(businessId)}/balance/loads/${loadId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['private-events-loads', 'private-events-balance'],
    }),
    getPrivateEventConfig: build.query<PrivateEventConfigDTO | null, OptionalBusinessContext>({
      providesTags: ['private-events-config'],
      query: ({ businessId }) => ({
        url: getPrivateEventConfigPath(businessId),
        method: 'GET',
      }),
      transformResponse: (response: Response<PrivateEventConfigDTO>): PrivateEventConfigDTO | null => response.data || null,
    }),
    // Public: the onboarding quotes the price per credit before the account exists.
    getPublicCreditPrice: build.query<PublicCreditPriceDTO | null, void>({
      query: () => ({
        url: '/private-event-config/price',
        method: 'GET',
      }),
      transformResponse: (response: Response<PublicCreditPriceDTO>): PublicCreditPriceDTO | null => response.data || null,
    }),
    updatePrivateEventConfig: build.mutation<PrivateEventConfigDTO | null, UpdatePrivateEventConfigDTO>({
      query: (dto) => ({
        url: '/private-event-config',
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: ['private-events-config'],
      transformResponse: (response: Response<PrivateEventConfigDTO>): PrivateEventConfigDTO | null => response.data || null,
    }),
  }),
});

export const {
  useGetPrivateEventsBalanceQuery,
  useGetPrivateEventsBalanceByEventQuery,
  useGetPrivateEventsLoadsQuery,
  useCreatePrivateEventsLoadMutation,
  useDeletePrivateEventsLoadMutation,
  useGetPrivateEventConfigQuery,
  useUpdatePrivateEventConfigMutation,
  useGetPublicCreditPriceQuery,
} = privateEventsApi;

export { privateEventsApi };
